import Link from 'next/link'
import React, { useState } from 'react'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { GOOGLE_EVENTS, trackEvent } from '@/shared/analytics'
import { extractApiError } from '@/shared/api/extractApiError'
import { useAccountAuthMagicLinkRequestCreate } from '@/shared/api/generated/authentication/authentication'
import { TextField } from '@/shared/components/TextField/TextField'
import { pxToRem } from '@/shared/styles/themes'
import { faEnvelopeOpenText } from '@fortawesome/pro-light-svg-icons/faEnvelopeOpenText'
import { Alert, Box, styled, Typography } from '@mui/material'

/** Where the magic link lands: back on the cart, which then continues to Stripe. */
export const CART_RESUME_PATH = '/cart?checkout=1'

interface CartLoginPanelProps {
  /** Same-origin path the magic link returns to (defaults to the cart, which then continues to Stripe) */
  returnPath?: string
  title?: string
  description?: React.ReactNode
  /** Called once the link has been sent (e.g. to remember the total the shopper saw) */
  onLinkSent?: () => void
}

/**
 * Inline sign-in for checkout. Checkout needs an account (purchases are
 * granted to a user), so logged-out shoppers get a magic link that brings
 * them straight back. The cart itself is in localStorage, so it survives the
 * round-trip. The site's /account/login page ignores return paths, which is
 * why this uses the magic-link `next` hand-off instead.
 */
export const CartLoginPanel: React.FC<CartLoginPanelProps> = ({
  returnPath = CART_RESUME_PATH,
  title = 'Log in to check out',
  description = 'Your purchases are added to your Pixie World account. Enter your email and we\'ll send you a secure sign-in link.',
  onLinkSent,
}) => {
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const requestMagicLink = useAccountAuthMagicLinkRequestCreate()

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()

    const trimmed = email.trim()

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('That email address doesn\'t look quite right. Please double-check and try again.')

      return
    }

    setError(null)

    try {
      await requestMagicLink.mutateAsync({
        data: {
          email: trimmed,
          redirect_url: `${window.location.origin}/auth/verify?next=${encodeURIComponent(returnPath)}`,
        },
      })
      trackEvent(GOOGLE_EVENTS.MAGIC_LINK_REQUESTED, { source: 'checkout' })
      onLinkSent?.()
      setSentTo(trimmed)
    } catch (err) {
      setError(extractApiError(err, 'We couldn\'t send the sign-in link. Please try again.'))
    }
  }

  if (sentTo) {
    return (
      <StyledPanel>
        <Icon icon={faEnvelopeOpenText} sx={{ fontSize: pxToRem(36), color: 'primary.main' }} />
        <Typography variant="subtitle1" fontWeight={600}>
          Check your email
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We sent a sign-in link to <strong>{sentTo}</strong>. Open it on this device and
          you&apos;ll come straight back here. Your cart is saved.
        </Typography>
        <Button text size="small" onClick={() => setSentTo(null)}>
          Use a different email
        </Button>
      </StyledPanel>
    )
  }

  return (
    <StyledPanel component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      <TextField
        label="Email address"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        size="large"
        required
        autoFocus
        fullWidth
        inputProps={{ inputMode: 'email', autoComplete: 'email' }}
      />
      {error && (
        <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
      )}
      <Button primary type="submit" fullWidth disabled={requestMagicLink.isPending}>
        {requestMagicLink.isPending ? 'Sending…' : 'Email me a sign-in link'}
      </Button>
      <Typography variant="caption" color="text.secondary">
        Have a password?{' '}
        <Link href="/account/login">Sign in here</Link>
        {' '}— your cart will be waiting.
      </Typography>
    </StyledPanel>
  )
}

const StyledPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: pxToRem(12),
  padding: pxToRem(20),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: pxToRem(12),
  textAlign: 'center',
})) as typeof Box
